import * as React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { Reservation } from 'models/reservation';
import { Product } from 'models/product';
import {
  todayIsInParticipationRuleRange,
  todayIsParticipationDate,
} from 'lib/util/getCheckinErrorReason';
import config from 'config';

export const useETicketRedemptionErrorReason = (
  reservation: Reservation | undefined,
  product: Product | null
) => {
  const { t } = useTranslation();

  const [response, setResponse] = React.useState<{
    readyToCheckin: boolean;
    reason?: string | null;
  }>({
    readyToCheckin: false,
    reason: null,
  });

  const noteForRedemptionModalBeforeCheckinStartTime =
    product?.qr_checkin_settings?.note_for_redemption_modal_before_checkin_start_time ??
    t('Ticket is not available until the redemption start time.');

  const calculateParticipationTime = (): moment.Moment | null => {
    if (!reservation || !product?.qr_checkin_settings?.checkin_start_time_relative) return null;

    const participationTime = moment.tz(
      reservation.start_date_time_utc,
      reservation.start_timezone ?? ''
    );
    const match = product.qr_checkin_settings.checkin_start_time_relative.match(
      /^([+-]?)(\d+):(\d+)$/
    );
    if (!match) throw new Error('Invalid offset format');

    const [, signSymbol, hoursStr, minutesStr] = match;
    const sign = signSymbol === '-' ? -1 : 1;
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    participationTime.add(sign * hours, 'hours').add(sign * minutes, 'minutes');
    return participationTime;
  };

  React.useEffect(() => {
    if (!product || !reservation) return;

    try {
      if (reservation.status !== 'CONFIRMED') {
        setResponse({
          readyToCheckin: false,
          reason: t('Ticket is not available.'),
        });
        return;
      }

      const checkinStatus = reservation?.checkin_info?.checkin_status;
      const isExpired = reservation?.checkin_info?.expiration_date_time_utc
        ? moment().isAfter(
            moment.tz(
              reservation?.checkin_info?.expiration_date_time_utc,
              reservation?.start_timezone ?? 'UTC'
            )
          )
        : false;

      if (checkinStatus !== 'NOT_CHECKED_IN' && !isExpired) {
        setResponse({
          readyToCheckin: true,
        });
        return;
      } else {
        if (
          config.enableCheckinStartTimeRelative &&
          !product?.booking_widget_settings?.is_free_start_date_time &&
          product?.qr_checkin_settings?.checkin_start_time_relative
        ) {
          const participationTime = calculateParticipationTime();
          if (!participationTime) return;

          if (!moment().isAfter(participationTime)) {
            const checkReadyToCheckin = () => {
              if (moment().isAfter(participationTime)) {
                setResponse({ readyToCheckin: true });
              } else {
                setResponse({
                  readyToCheckin: false,
                  reason: noteForRedemptionModalBeforeCheckinStartTime,
                });
              }
            };

            checkReadyToCheckin();
            const interval = setInterval(checkReadyToCheckin, 60 * 1000);
            return () => clearInterval(interval);
          }
        } else {
          if (reservation?.free_start_date_time_from && reservation?.free_start_date_time_to) {
            if (!todayIsInParticipationRuleRange(reservation)) {
              setResponse({
                readyToCheckin: false,
                reason: t('Ticket is available during the participation period.'),
              });
              return;
            }
          } else {
            if (reservation && !todayIsParticipationDate(reservation)) {
              setResponse({
                readyToCheckin: false,
                reason: t('Ticket is available only on the day of participation.'),
              });
              return;
            }
          }
        }
      }

      setResponse({ readyToCheckin: true });
    } catch (e) {
      console.error(e);
      setResponse({ readyToCheckin: true });
    }
  }, [product, reservation]);

  return response;
};
